import { ref } from 'vue'

/**
 * Composable for paginated list fetching with offset-based pagination.
 *
 * @param {Function} fetchFn - Async function that receives { limit, offset, ...filters } and returns
 *   either { data: [], pagination: { total } } (standard backend response via apiClient)
 *   or a plain array (fallback for endpoints without pagination).
 * @param {Object} options
 * @param {number} options.pageSize - Items per page (default 25)
 * @param {boolean} options.immediate - Fetch on creation (default true)
 *
 * @returns {{ items: Ref, total: Ref, loading: Ref, offset: Ref, limit: Ref, nextCursor: Ref, fetch: Function, resetAndFetch: Function }}
 */
export function usePaginatedList(fetchFn, { pageSize = 25, immediate = true } = {}) {
  const items = ref([])
  const total = ref(0)
  const loading = ref(false)
  const offset = ref(0)
  const limit = ref(pageSize)
  const nextCursor = ref(null) // Captured from cursor-mode responses (pagination.next_cursor)
  const filters = ref({})

  const fetch = async () => {
    try {
      loading.value = true
      const response = await fetchFn({
        limit: limit.value,
        offset: offset.value,
        ...filters.value,
      })

      // Handle paginated response: { data: [], pagination: { total, next_cursor? } }
      if (response && response.pagination) {
        items.value = response.data || []
        total.value = response.pagination.total || 0
        nextCursor.value = response.pagination.next_cursor || null
      } else if (Array.isArray(response)) {
        // Fallback: plain array (no pagination from backend)
        items.value = response
        total.value = response.length
      } else {
        items.value = []
        total.value = 0
      }
    } catch (error) {
      console.error('usePaginatedList fetch error:', error)
      items.value = []
      total.value = 0
    } finally {
      loading.value = false
    }
  }

  /**
   * Reset offset to 0 and fetch with new filters.
   * Call this when search/filter/sort changes.
   */
  const resetAndFetch = (newFilters = {}) => {
    filters.value = { ...filters.value, ...newFilters }
    offset.value = 0
    return fetch()
  }

  if (immediate) {
    fetch()
  }

  return {
    items,
    total,
    loading,
    offset,
    limit,
    nextCursor,
    fetch,
    resetAndFetch,
  }
}
